import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { 
  LayoutDashboard, 
  FileText, 
  Image, 
  Megaphone, 
  Radio, 
  DollarSign,
  LogOut,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import ContentManager from './ContentManager';
import PagesManager from './PagesManager';
import GalleryManager from './GalleryManager';
import AnnouncementManager from './AnnouncementManager';
import LivestreamManager from './LivestreamManager';
import DonationViewer from './DonationViewer';
import FormsManager from './FormsManager';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    announcements: 0,
    photos: 0,
    donations: 0,
    content: 0
  });

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    const userStr = localStorage.getItem('adminUser');
    
    if (!token || !userStr) {
      navigate('/admin');
      return;
    }

    try {
      await api.get('/admin/verify');
      setUser(JSON.parse(userStr));
    } catch (error) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      navigate('/admin');
      toast.error('Session expired. Please login again.');
    }
  };

  const fetchStats = async () => {
    try {
      const [announcements, gallery, content] = await Promise.all([
        api.get('/announcements'),
        api.get('/gallery'),
        api.get('/content')
      ]);
      
      setStats({
        announcements: announcements.data.length,
        photos: gallery.data.length,
        content: content.data.length,
        donations: 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  // Translation function placeholder - AdminDashboard doesn't use language context yet
  // but tabs remain in English as admin interface is typically English
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'pages', label: 'All Pages', icon: FileText },
    { id: 'forms', label: 'Form Submissions', icon: FileText },
    { id: 'content', label: 'Advanced Content', icon: FileText },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
    { id: 'livestream', label: 'Live Stream', icon: Radio },
    { id: 'donations', label: 'Donations', icon: DollarSign }
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{fontFamily: 'Playfair Display, serif'}}>
                Admin Dashboard
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Welcome back, {user?.username || 'Admin'}
              </p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center space-x-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-8">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-amber-600 text-amber-600 dark:text-amber-500'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <div>
          {activeTab === 'pages' && <PagesManager onUpdate={fetchStats} />}
          {activeTab === 'forms' && <FormsManager />}
          {activeTab === 'overview' && (
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Content Pages</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.content}</p>
                  </div>
                  <FileText className="w-12 h-12 text-amber-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Announcements</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.announcements}</p>
                  </div>
                  <Megaphone className="w-12 h-12 text-blue-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Gallery Photos</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.photos}</p>
                  </div>
                  <Image className="w-12 h-12 text-green-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Donations</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">${stats.donations}</p>
                  </div>
                  <DollarSign className="w-12 h-12 text-purple-600" />
                </div>
              </Card>

              <Card className="p-6 md:col-span-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <Button onClick={() => setActiveTab('announcements')} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    New Announcement
                  </Button>
                  <Button onClick={() => setActiveTab('gallery')} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                  <Button onClick={() => setActiveTab('livestream')} className="bg-red-600 hover:bg-red-700">
                    <Radio className="w-4 h-4 mr-2" />
                    Manage Stream
                  </Button>
                  <Button onClick={() => setActiveTab('content')} className="bg-amber-600 hover:bg-amber-700">
                    <FileText className="w-4 h-4 mr-2" />
                    Edit Content
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'content' && <ContentManager onUpdate={fetchStats} />}
          {activeTab === 'gallery' && <GalleryManager onUpdate={fetchStats} />}
          {activeTab === 'announcements' && <AnnouncementManager onUpdate={fetchStats} />}
          {activeTab === 'livestream' && <LivestreamManager />}
          {activeTab === 'donations' && <DonationViewer />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;